import { cloneDeep } from 'lodash'
import mixpanel from 'mixpanel-browser'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useRecoilState } from 'recoil'
import {
    ContentActionView,
    ContentView,
    EnrichedContent,
    ProfileViewWithShadowBan,
    TranslatedTextView,
    useDetectLanguagesQuery,
    useEnrichContentQuery,
    useGetProfilesByIdsQuery,
    useTranslateLazyQuery
} from '../../api/client'
import { targetLanguageState, translateAllState } from '../../store/UIStore'
import { toastError } from '../Notification'

export type ContentCardPlumbing = {
    profileCache: ProfileViewWithShadowBan[]
    actionCacheMap: Map<number, ContentActionView[]>
    onContentAction: (actions: ContentActionView[], queued?: boolean) => void
    enrichedContentMap: Map<number, EnrichedContent>
    enriching: boolean
    detectedLanguageMap: Map<string, string | null>
    translationCache: Map<string, TranslatedTextView>
    translatingTexts: Set<string>
    onTranslateItem: (text: string) => void
    onMarkAsReadClick: (content: ContentView) => void
    onMarkAsUnreadClick: (content: ContentView) => void
    onShadowBanChange: (contentId: number, profileId: number) => void
    newProfileIds: Map<number, number>
}

export type ContentCardPlumbingOptions = {
    // Passed to the profiles query so it returns shadow-ban status, which is per account
    // and so only meaningful when the cards all come from one account.
    singleAccountId?: number
    // A card has already written its read receipt when it calls onMarkAsRead/UnreadClick;
    // the host re-runs its own query so the card's read state comes back from the server.
    onReadReceiptChange?: () => void
}

/**
 * Everything a ContentCard needs beyond its content: profiles, enrichment, language
 * detection and translation, read-receipt callbacks and the action cache. It was lifted
 * out of ContentList so every surface that renders cards (the feed, the conversation
 * drawer, a post's conversation) behaves the same when a card is actioned.
 *
 * The action cache is reseeded from `contents` whenever its identity changes, which is
 * how a refetch (e.g. after hide-all) replaces optimistic toggles with server state.
 * Callers must therefore keep `contents` referentially stable between fetches (memoise
 * it); a fresh array each render would reseed, and re-render, forever.
 *
 * A reseed keeps a card's cached (possibly optimistic) actions while that card's
 * `actions` array keeps its reference. The feed's results are `no-cache`, so every
 * refetch hands over new arrays and resets as before; Apollo-normalised surfaces (the
 * post page's Threaded tab, the drawer) reset a card only when the server copy changes.
 */
export function useContentCardPlumbing(
    contents: ContentView[],
    opts?: ContentCardPlumbingOptions
): ContentCardPlumbing {
    const singleAccountId = opts?.singleAccountId
    const [targetLanguage] = useRecoilState(targetLanguageState)

    // We have an action cache as actions we can change the actions for an item of content
    // without reloading that content. This means we need to maintain it as state.
    const [actionCache, setActionCache] = useState<Map<number, ContentActionView[]>>(
        new Map<number, ContentActionView[]>()
    )

    /** ******************************************************
    GET PROFILES
    */

    // Fetch the profiles
    // Pass the account ID if we're viewing content from a single account to get shadow ban status
    const {
        data: profileData,
        refetch: refetchProfiles
        // Errors handled by the error callback below. Loading display is via the default avatar profile image - it simply gets replaced if/when an image is loaded.
    } = useGetProfilesByIdsQuery({
        variables: {
            profileIds: contents.map((content) => content.authorProfileId || -1),
            accountId: singleAccountId
        },

        onError: (error) => {
            console.error('There was an error fetching profiles', error)
            toastError('There was an error fetching profiles.')
        }
    })

    const profiles = profileData?.getProfilesByIds || []

    // Track newly created profile IDs that need to be included in profile fetches
    const [newProfileIds, setNewProfileIds] = useState<Map<number, number>>(new Map())

    // Callback to refetch profiles after shadow ban status changes
    const handleShadowBanChange = async (contentId: number, profileId: number) => {
        // Track the new profile ID for this content so it's included in the refetch
        const updatedNewProfileIds = new Map(newProfileIds).set(contentId, profileId)
        setNewProfileIds(updatedNewProfileIds)

        // Build the profile IDs list, including any newly created profiles from previous shadow bans
        const existingProfileIds = contents.map((content) => content.authorProfileId || -1)
        const previouslyCreatedProfileIds = Array.from(updatedNewProfileIds.values())
        const allProfileIds = [...new Set([...existingProfileIds, ...previouslyCreatedProfileIds])]

        await refetchProfiles({ profileIds: allProfileIds, accountId: singleAccountId })
    }

    /** ******************************************************
    ENRICH CONTENT - this fetches additional data from the social media services
    */

    const contentsToEnrich = contents.map((content) => {
        return {
            // Mandatory fields required to perform enrichment
            id: content.id,
            accountId: content.account.id,
            socialMediaServiceShortName: content.socialMediaServiceName,

            // Fields that might need enriching
            serviceContentId: content.serviceContentId,
            serviceAuthorId: content.serviceAuthorId,
            serviceAuthorName: content.serviceAuthorName,
            serviceAuthorUsername: content.serviceAuthorDisplayUsername,
            originalPostLinkId: content.socialMediaContentLink,
            originalPostId: content.originalPostId
        }
    })

    // We need to now enrich the content
    const { data: enrichedContentResults, loading: loadingEnrichedContentResults } =
        useEnrichContentQuery({
            variables: {
                contents: contentsToEnrich
            },
            onError: (error) => {
                console.error('There was an error connecting to the server', error)
                toastError('There was an error connecting to the server. ')
            }
        })

    const enrichedContents = enrichedContentResults?.enrichContent || []

    /** ******************************************************
    DETECT LANGUAGES (local, free via lingua)
    */
    const [translateAll] = useRecoilState(translateAllState)

    const { data: detectedLanguageData } = useDetectLanguagesQuery({
        variables: {
            sourceTexts: contents.map((content) => content.content)
        },
        onError: (error) => {
            console.error('Error detecting languages', error)
            toastError(
                'Unable to detect content languages. Translation features may be unavailable.'
            )
        }
    })

    const detectedLanguages = useMemo(
        () => detectedLanguageData?.detectLanguages ?? [],
        [detectedLanguageData]
    )

    /** ******************************************************
    TRANSLATE CONTENT (on-demand via Google API)
    */
    const [translateQuery] = useTranslateLazyQuery({
        onError: (error) => {
            console.error('There was an error connecting to the server', error)
            toastError('There was an error connecting to the server. ')
        }
    })

    // Cache translations in state so they survive re-renders and toggle off/on
    const [translationCache, setTranslationCache] = useState<Map<string, TranslatedTextView>>(
        new Map()
    )
    // Track which specific texts are currently being translated, so only those cards show a spinner
    const [translatingTexts, setTranslatingTexts] = useState<Set<string>>(new Set())
    // Ref to avoid stale closure in the translateAll effect without re-triggering it on every cache update
    const translationCacheRef = useRef(translationCache)
    translationCacheRef.current = translationCache

    // Clear translation cache when target language changes, so stale translations
    // from a previous language don't prevent re-translation to the new language
    useEffect(() => {
        setTranslationCache(new Map())
    }, [targetLanguage])

    // "Translate all" handler: translate all non-matching items at once
    useEffect(() => {
        if (!translateAll || detectedLanguages.length === 0) return

        const textsNeedingTranslation = detectedLanguages
            .filter(
                (dl) =>
                    dl.detectedSourceLanguage &&
                    dl.detectedSourceLanguage !== targetLanguage &&
                    !translationCacheRef.current.has(dl.sourceText)
            )
            .map((dl) => dl.sourceText)

        if (textsNeedingTranslation.length === 0) return

        setTranslatingTexts((prev) => new Set([...prev, ...textsNeedingTranslation]))
        translateQuery({
            variables: {
                sourceTexts: textsNeedingTranslation,
                targetLanguage: targetLanguage
            }
        })
            .then((result) => {
                setTranslatingTexts((prev) => {
                    const updated = new Set(prev)
                    textsNeedingTranslation.forEach((t) => updated.delete(t))
                    return updated
                })
                if (result.error) {
                    console.error('Translate all failed', result.error)
                    toastError('Translation failed. Please try again.')
                    return
                }
                if (result.data?.translate) {
                    setTranslationCache((prev) => {
                        const updated = new Map(prev)
                        result.data!.translate.forEach((t) => {
                            updated.set(t.sourceText, t)
                        })
                        return updated
                    })
                }
            })
            .catch((err) => {
                console.error('Unexpected error in translate all', err)
                toastError('Translation failed. Please try again.')
                setTranslatingTexts((prev) => {
                    const updated = new Set(prev)
                    textsNeedingTranslation.forEach((t) => updated.delete(t))
                    return updated
                })
            })
    }, [translateAll, detectedLanguages, targetLanguage, translateQuery])

    // Handler for single-item translation, passed down to ContentCard
    const handleTranslateItem = async (sourceText: string) => {
        if (translationCache.has(sourceText)) return
        setTranslatingTexts((prev) => new Set([...prev, sourceText]))
        try {
            const result = await translateQuery({
                variables: {
                    sourceTexts: [sourceText],
                    targetLanguage: targetLanguage
                }
            })
            if (result.data?.translate?.[0]) {
                setTranslationCache((prev) => {
                    const updated = new Map(prev)
                    updated.set(sourceText, result.data!.translate[0])
                    return updated
                })
            } else if (result.error) {
                console.error('Translation failed for item', result.error)
                toastError('Translation failed. Please try again.')
            } else {
                toastError('Translation failed. Please try again.')
            }
        } catch (err) {
            console.error('Unexpected error translating item', err)
            toastError('Translation failed. Please try again.')
        } finally {
            setTranslatingTexts((prev) => {
                const updated = new Set(prev)
                updated.delete(sourceText)
                return updated
            })
        }
    }

    /** ******************************************************
    READ RECEIPT - per card. The card writes the receipt itself; we only ask the host to refetch.
    The bulk read/unread mutations stay with the host, which owns the selection.
    */

    const handleMarkAsReadClick = async function () {
        opts?.onReadReceiptChange?.()
    }

    const handleMarkAsUnreadClick = async function () {
        opts?.onReadReceiptChange?.()
    }

    /** ******************************************************
    ACTION CACHE
    */

    // The `actions` array each card was last seeded from, so a reseed can tell a card the
    // server has re-sent from one that is merely still on screen.
    const seededActionsRef = useRef<Map<number, ContentActionView[]>>(new Map())

    // Update action cache when results change
    useEffect(() => {
        // A card whose content still carries the very `actions` array it was seeded from
        // keeps its cached (possibly optimistically toggled) actions. Without this, a
        // surface that grows its list in place (the post page's "Show more comments" and
        // "View N more replies") would flip every action already taken on screen back to
        // the stale server copy, because actions never write to the Apollo cache. A
        // refetch hands over new arrays, so it still replaces optimistic toggles with
        // server state, as ContentList relies on.
        const seeded = seededActionsRef.current
        setActionCache((previous) => {
            const newActions = new Map<number, ContentActionView[]>()
            for (const content of contents) {
                const unchanged = seeded.get(content.id) === content.actions
                newActions.set(
                    content.id,
                    (unchanged && previous.get(content.id)) || content.actions
                )
            }
            return newActions
        })
        seededActionsRef.current = new Map(contents.map((content) => [content.id, content.actions]))
    }, [contents])

    const handleContentAction = function (actions: ContentActionView[]) {
        // Lets make sure we track these actions in our product analytics tool
        for (const action of actions) {
            mixpanel.track(`Content action - ${action.action} - ${action.active ? 'on' : 'off'}`, {
                action
            })
        }

        const contentIdsToUpdate = actions.map((a) => a.contentId)

        console.info('Updating content ids', actions)

        // This action has already been performed by the button but lets update the cache to reflect this.
        for (const contentId of contentIdsToUpdate) {
            const contentActions = actionCache.get(contentId) || []

            // Update the content based on the current action
            const updatedActions = cloneDeep(contentActions)

            const newActions = actions.filter((a) => (a.contentId = contentId))

            for (const newAction of newActions) {
                // Does this action already exist?
                const indexOfExistingAction: number = updatedActions.findIndex(
                    (existingAction) => existingAction.id === newAction.id
                )

                if (indexOfExistingAction >= 0) {
                    // ... it does - toggle the action
                    updatedActions[indexOfExistingAction].active =
                        !updatedActions[indexOfExistingAction].active

                    // Also update the message if it exists
                    if (newAction.message) {
                        updatedActions[indexOfExistingAction].message = newAction.message
                    }
                } else {
                    updatedActions.push(newAction)
                }
            }
            const modifiedContentActions = new Map<number, ContentActionView[]>()
            modifiedContentActions.set(contentId, updatedActions)

            setActionCache((actionCacheState) => {
                return new Map<number, ContentActionView[]>([
                    ...Array.from(actionCacheState.entries()),
                    ...Array.from(modifiedContentActions.entries())
                ])
            })
        }
    }

    // The key here is the content id - we convert to a map as it is a faster lookup.
    const enrichedContentMap = new Map<number, EnrichedContent>(
        enrichedContents.map((enrichedContent) => [enrichedContent.id, enrichedContent])
    )

    const detectedLanguageMap = new Map<string, string | null>(
        detectedLanguages.map((dl) => [dl.sourceText, dl.detectedSourceLanguage || null])
    )

    return {
        profileCache: profiles,
        actionCacheMap: actionCache,
        onContentAction: handleContentAction,
        enrichedContentMap,
        enriching: loadingEnrichedContentResults,
        detectedLanguageMap,
        translationCache,
        translatingTexts,
        onTranslateItem: handleTranslateItem,
        onMarkAsReadClick: handleMarkAsReadClick,
        onMarkAsUnreadClick: handleMarkAsUnreadClick,
        onShadowBanChange: handleShadowBanChange,
        newProfileIds
    }
}
